import { useEffect, useState, type FormEvent } from 'react'
import { useParams } from 'react-router-dom'
import { api, setRecipientCsrf } from '../api/client'
import { ApiError } from '../api/types'
import type { RecipientObjectView } from '../api/types'
import { PageShell } from '../components/BrandChrome'
import { Alert, Button, Card, Input, Label, Subtitle, Title } from '../components/ui'
import { errorMessage, formatBytes } from '../lib/format'
import { formatPhoneHint } from '../lib/phone'

const RESEND_COOLDOWN_SEC = 30
const DEFAULT_LOCKOUT_SEC = 30 * 60

function isAuthFailure(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 401 || err.status === 403)
}

export function RecipientPage() {
  const { dropId = '' } = useParams()
  const [phoneLast4, setPhoneLast4] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [unlocked, setUnlocked] = useState(false)
  const [objects, setObjects] = useState<RecipientObjectView[] | null>(null)
  const [totalBytes, setTotalBytes] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [cooldown, setCooldown] = useState(0)
  const [zipDisabled, setZipDisabled] = useState(false)

  function relock(message: string) {
    setUnlocked(false)
    setObjects(null)
    setTotalBytes(0)
    setRecipientCsrf(null)
    setCode('')
    setError(message)
    setInfo(null)
  }

  useEffect(() => {
    if (cooldown <= 0) return
    const t = window.setTimeout(() => setCooldown((c) => Math.max(0, c - 1)), 1000)
    return () => window.clearTimeout(t)
  }, [cooldown])

  useEffect(() => {
    if (!dropId) return
    let cancelled = false
    ;(async () => {
      try {
        await api.recipientSession(dropId)
        if (cancelled) return
        const list = await api.recipientObjects(dropId)
        if (cancelled) return
        setUnlocked(true)
        setObjects(list.objects)
        setTotalBytes(list.total_bytes)
      } catch {
        // no existing recipient session — stay on OTP gate
      }
    })()
    return () => {
      cancelled = true
    }
  }, [dropId])

  async function sendOtp() {
    if (!dropId || cooldown > 0) return
    setError(null)
    setInfo(null)
    setBusy(true)
    try {
      const res = await api.otpSend(dropId)
      setPhoneLast4(res.phone_last4)
      setInfo('A one-time code was sent by text message.')
      setCooldown(RESEND_COOLDOWN_SEC)
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        const wait = err.retryAfterSeconds ?? (
          err.detail === 'otp_locked' ? DEFAULT_LOCKOUT_SEC : RESEND_COOLDOWN_SEC
        )
        setCooldown(wait)
        if (err.detail === 'otp_locked') {
          setError(
            `Too many incorrect codes. Verification is locked for about ${Math.ceil(wait / 60)} minutes.`,
          )
        } else {
          setError(`Too many codes requested. Try again in about ${wait}s.`)
        }
      } else {
        setError(errorMessage(err))
      }
    } finally {
      setBusy(false)
    }
  }

  async function onVerify(e: FormEvent) {
    e.preventDefault()
    if (!dropId) return
    setError(null)
    setBusy(true)
    try {
      await api.otpVerify(dropId, code.trim())
      const list = await api.recipientObjects(dropId)
      setUnlocked(true)
      setObjects(list.objects)
      setTotalBytes(list.total_bytes)
      setInfo(null)
    } catch (err) {
      if (err instanceof ApiError && err.status === 429 && err.detail === 'otp_locked') {
        const wait = err.retryAfterSeconds ?? DEFAULT_LOCKOUT_SEC
        setCooldown(wait)
        setError(
          `Too many incorrect codes. Verification is locked for about ${Math.ceil(wait / 60)} minutes.`,
        )
      } else {
        setError(errorMessage(err))
      }
    } finally {
      setBusy(false)
    }
  }

  async function downloadObject(objectId: string) {
    if (!dropId) return
    setError(null)
    try {
      const sas = await api.downloadObjectSas(dropId, objectId)
      window.location.assign(sas.url)
    } catch (err) {
      if (isAuthFailure(err)) {
        relock('Your secure session expired. Request a new code to continue.')
        return
      }
      setError(errorMessage(err))
    }
  }

  async function downloadZip() {
    if (!dropId) return
    setError(null)
    try {
      const sas = await api.downloadZipSas(dropId)
      window.location.assign(sas.url)
    } catch (err) {
      if (isAuthFailure(err)) {
        relock('Your secure session expired. Request a new code to continue.')
        return
      }
      if (err instanceof ApiError && err.status === 409) {
        setZipDisabled(true)
        setError('Zip download is not available for this drop (size or capability limit).')
      } else {
        setError(errorMessage(err))
      }
    }
  }

  return (
    <PageShell homeTo={`/d/${dropId}`}>
      <Card className="mx-auto max-w-lg">
        <Title>Secure drop</Title>
        <Subtitle>
          {phoneLast4
            ? `Enter the code sent to ${formatPhoneHint(phoneLast4)}.`
            : 'Verify with a one-time code sent to the phone on file for this drop.'}
        </Subtitle>

        {error ? <Alert className="mb-3">{error}</Alert> : null}
        {info ? (
          <Alert tone="info" className="mb-3">
            {info}
          </Alert>
        ) : null}

        {!unlocked ? (
          <div className="space-y-4">
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              disabled={busy || cooldown > 0}
              onClick={() => void sendOtp()}
            >
              {cooldown > 0 ? `Resend available in ${cooldown}s` : 'Send code'}
            </Button>
            <form className="space-y-3" onSubmit={onVerify}>
              <div>
                <Label htmlFor="otp">One-time code</Label>
                <Input
                  id="otp"
                  name="otp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={10}
                  required
                  value={code}
                  onChange={(ev) => setCode(ev.target.value)}
                  className="text-center text-2xl tracking-[0.35em]"
                  aria-describedby="otp-help"
                />
                <p id="otp-help" className="mt-1 text-xs text-slate-500">
                  6-digit code from your text message.
                </p>
              </div>
              <Button type="submit" disabled={busy || code.trim().length < 4} className="w-full">
                {busy ? 'Checking…' : 'Verify and open files'}
              </Button>
            </form>
          </div>
        ) : (
          <div className="space-y-4">
            <Alert tone="success">Verified. Downloads use short-lived links only.</Alert>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-slate-600">
                {objects?.length ?? 0} file(s) · {formatBytes(totalBytes)}
              </p>
              <Button
                type="button"
                variant="secondary"
                disabled={zipDisabled}
                onClick={() => void downloadZip()}
              >
                Download zip
              </Button>
            </div>
            <ul className="divide-y rounded-lg border border-slate-200">
              {(objects ?? []).map((o) => (
                <li key={o.object_id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-3">
                  <div className="min-w-0">
                    <div className="break-all font-mono text-xs">{o.relative_path}</div>
                    <div className="text-xs text-slate-500">{formatBytes(o.size_bytes)}</div>
                  </div>
                  <Button type="button" onClick={() => void downloadObject(o.object_id)}>
                    Download
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>
    </PageShell>
  )
}
